import React, { useState, useEffect, useMemo } from 'react';
import {
  AppBar, Toolbar, Typography, IconButton,
  Badge, Menu, MenuItem, Box, Button, Avatar, Chip,
  Drawer, List, ListItem, ListItemIcon, ListItemText,
  Divider, useMediaQuery, useTheme, Tooltip
} from '@mui/material';
import { LapisticsLogo } from '../pages/Landing';
import {
  Notifications, Dashboard,
  People, Event, Timer, Person, Logout, HowToReg,
  EmojiEvents, FactCheck, WorkspacePremium, Tune, Shield, LocationOn, SwapHoriz,
  Menu as MenuIcon, Close as CloseIcon, HelpOutline,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { getNotifications, markNotificationRead, getClubLogo, getClub } from '../api/api';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Builds a safe <img> src for a club logo value coming from the API.
// Logos are stored as full base64 data URLs going forward (see clubs.py
// upload_logo) precisely BECAUSE Render's filesystem is ephemeral and wipes
// /uploads on every deploy — a relative-path logo can never reliably survive.
// Some older club rows (seeded before that migration, e.g. the public demo
// club) still carry one of those legacy relative paths ("uploads/logo123.png",
// no leading slash). Blindly doing `${API_URL}${logo_url}` on one of those
// produced a malformed, always-404 URL — and even a correctly-formed URL
// would still 404 because the underlying file no longer exists on disk. So
// treat anything that isn't a data: URL as unusable and fall back to the
// placeholder rather than requesting a guaranteed-broken image.
const resolveLogoSrc = (logoUrl) => (logoUrl && logoUrl.startsWith('data:')) ? logoUrl : null;

// Module-level logo cache — keyed by clubId, survives re-renders, cleared on logout.
// Prevents re-fetching a 1-2MB base64 blob on every component mount.
// Pre-seed from localStorage so logo is available on frame 1 after login.
const _logoCache = (() => {
  try {
    const c = localStorage.getItem('club');
    if (c) {
      const parsed = JSON.parse(c);
      if (parsed.id && parsed.logo_url) return { [parsed.id]: parsed.logo_url };
    }
  } catch {}
  return {};
})();

export default function Navbar() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { user: authUser, logout, clubId: authContextClubId, selectedClubId, selectClub } = useAuth();
  const user = useMemo(() => authUser || {}, [authUser]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [notifications,    setNotifications]    = useState([]);
  const [anchorEl,         setAnchorEl]         = useState(null);
  const [notifAnchor,      setNotifAnchor]      = useState(null);
  const [currentTime,      setCurrentTime]      = useState(new Date());
  const [clubInfo,         setClubInfo]         = useState(() => {
    // Seed from localStorage on first render — prevents flash of Lapistics logo/empty
    // state before the async /clubs/{id}/ fetch resolves.
    const c = localStorage.getItem('club');
    return c ? JSON.parse(c) : null;
  });
  const [logoUrl,          setLogoUrl]          = useState(() => {
    // Restore cached logo from localStorage on first render (survives page refresh)
    const c = localStorage.getItem('club');
    return c ? (JSON.parse(c).logo_url || null) : null;
  });
  const [drawerOpen,       setDrawerOpen]       = useState(false);

  // Derive selectedClubName from AuthContext.selectedClubId (the truth source).
  // Do NOT seed from localStorage directly — stale localStorage from a previous
  // session or a previous broken platform-enter flow would bleed through on login.
  // selectedClubId is null until the platform admin explicitly selects a club.
  const selectedClubName = user.is_platform_admin
    ? (selectedClubId ? (localStorage.getItem('selected_club_name') || clubInfo?.name || null) : null)
    : null;

  const isPlatformAdminNoClub = user.is_platform_admin && !selectedClubName;

  // Logo click — authenticated users go to dashboard
  const handleLogoClick = () => navigate('/dashboard');

  // ── Live Clock ──
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // ── Load Club Info (name, settings — lightweight, no logo blob) ──
  // Logo is fetched separately once and cached in _logoCache.
  useEffect(() => {
    const load = async () => {
      const cid = user.is_platform_admin ? null : authContextClubId;
      if (!cid) { setClubInfo(null); setLogoUrl(null); return; }
      try {
        // Use getClub (axios with withCredentials) — NOT raw fetch with Bearer null.
        // The old fetch sent 'Bearer null' which overrode the httpOnly cookie and
        // caused 401s, leading to a re-render flicker on every mount.
        const data = await getClub(cid);
        setClubInfo(data);
        // Merge lightweight fields into localStorage (preserve any cached logo_url)
        const stored = localStorage.getItem('club');
        const prev   = stored ? JSON.parse(stored) : {};
        localStorage.setItem('club', JSON.stringify({ ...prev, ...data }));
      } catch {
        // Network error — keep the localStorage seed in place (already in state)
      }

      // ── Fetch logo separately (cached per clubId) ──
      const cid2 = user.is_platform_admin ? null : authContextClubId;
      if (cid2) {
        if (_logoCache[cid2]) {
          // Cache hit — already in state from useState seed or previous render
          setLogoUrl(_logoCache[cid2]);
        } else {
          try {
            const logoData = await getClubLogo(cid2);
            _logoCache[cid2] = logoData;
            setLogoUrl(logoData);
            const stored = localStorage.getItem('club');
            const prev   = stored ? JSON.parse(stored) : {};
            localStorage.setItem('club', JSON.stringify({ ...prev, logo_url: logoData }));
          } catch { /* no logo uploaded — silent */ }
        }
      }
    };
    load();
  }, [user.is_platform_admin, authContextClubId]);

  // ── Listen for club updates (settings change, club switch) ──
  useEffect(() => {
    const handle = () => {
      const c = localStorage.getItem('club');
      if (c) { const parsed = JSON.parse(c); setClubInfo(parsed); if (parsed.logo_url) setLogoUrl(parsed.logo_url); }
    };
    // ── Listen for logo upload — update cache + state immediately ──
    const handleLogo = (e) => {
      const { clubId: cid, logo_url } = e.detail || {};
      if (cid && logo_url) {
        _logoCache[cid] = logo_url;
        setLogoUrl(logo_url);
        const stored = localStorage.getItem('club');
        const prev   = stored ? JSON.parse(stored) : {};
        localStorage.setItem('club', JSON.stringify({ ...prev, logo_url }));
      }
    };
    window.addEventListener('storage', handle);
    window.addEventListener('clubUpdated', handle);
    window.addEventListener('platformClubSelected', handle);
    window.addEventListener('logoUpdated', handleLogo);
    return () => {
      window.removeEventListener('storage', handle);
      window.removeEventListener('clubUpdated', handle);
      window.removeEventListener('platformClubSelected', handle);
      window.removeEventListener('logoUpdated', handleLogo);
    };
  }, []);

  // ── Notifications ──
  // FE-F3: pause polling when tab is hidden — same pattern as RaceDay poller.
  // Saves 6 requests/min per background tab on mobile/slow connections.
  useEffect(() => {
    loadNotifications();
    const iv = setInterval(() => {
      if (document.visibilityState === 'visible') loadNotifications();
    }, 10000);
    return () => clearInterval(iv);
  }, []);

  const loadNotifications = async () => {
    try {
      const data = await getNotifications();
      setNotifications(Array.isArray(data) ? data : (data.notifications || []));
    } catch {}
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  const handleNotifClick = async (notif) => {
    if (!notif.is_read) { await markNotificationRead(notif.id); loadNotifications(); }
    setNotifAnchor(null);
    if (notif.link) navigate(notif.link);
  };

  const handleLogout = () => {
    logout();
    // Clear logo cache so next login fetches fresh logo
    Object.keys(_logoCache).forEach(k => delete _logoCache[k]);
    ['club', 'selected_club_id', 'selected_club_name'].forEach(k =>
      localStorage.removeItem(k));
    navigate('/login');
  };

  const formatTime = (d) =>
    d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true })
    + ' · '
    + d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });

  // ── Nav items ──
  // Session 89: 'coach' was missing from almost every nav item below (only
  // /club-records's RoleGuard in App.js already allowed coach, but the nav
  // link itself was hidden even there). A user whose ONLY role is 'coach'
  // previously had ZERO visible nav items and effectively couldn't use the
  // app at all — every route they could reach required either the URL typed
  // directly (club-records) or an unguarded route with no link to click
  // (dashboard/events/race-day/results/my-profile). Added 'coach' everywhere
  // is_coach_or_above() grants that role read access on the backend.
  // Also added 'timekeeper' to Attendance — auth.py's is_attendance_marker_or_above()
  // already treats timekeeper as attendance-marker-or-above, but the nav item
  // (and the /attendance RoleGuard in App.js) excluded timekeeper entirely.
  const navItems = [
    { label: 'Dashboard',     path: '/dashboard',     roles: ['admin','coach','timekeeper','attendance_marker','viewer','swimmer','parent'] },
    { label: 'Members',       path: '/members',       roles: ['admin','coach'] },
    { label: 'Events',        path: '/events',        roles: ['admin','coach','timekeeper','attendance_marker','viewer'] },
    { label: 'Enter Events',  path: '/enter-events',  roles: ['swimmer','parent'] },
    // Session 97: promoted out of the My Profile "My Children" tab into its
    // own top-level nav item — Ram's feedback was that hiding a parent's
    // children inside their OWN profile page read backwards.
    { label: 'My Children',   path: '/my-children',   roles: ['parent'] },
    { label: 'Race Day',      path: '/race-day',      roles: ['admin','coach','timekeeper','attendance_marker','viewer','swimmer','parent'] },
    { label: 'Attendance',    path: '/attendance',    roles: ['admin','attendance_marker','timekeeper'] },
    { label: 'Results',       path: '/results',       roles: ['admin','coach','timekeeper','attendance_marker','viewer','swimmer','parent'] },
    { label: 'Season Awards', path: '/season-awards', roles: ['admin','coach','swimmer','viewer','parent'] },
    { label: 'Club Records',  path: '/club-records',  roles: ['admin','coach','swimmer','viewer','parent'] },
    { label: 'Club Settings', path: '/club-settings', roles: ['admin'] },
    { label: 'Audit Log',      path: '/audit-log',     roles: ['admin'] },
    { label: 'My Profile',    path: '/my-profile',    roles: ['admin','coach','timekeeper','attendance_marker','viewer','swimmer','parent'] },
  ];

  const userRoles   = (user.role || '').split(',').map(r => r.trim()).filter(Boolean);
  const visibleItems = navItems.filter(i => userRoles.some(r => i.roles.includes(r)));

  const go = (path) => { navigate(path); setDrawerOpen(false); };

  // ────────────────────────────────────────────
  // MOBILE DRAWER
  // ────────────────────────────────────────────
  const drawer = (
    <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}
      PaperProps={{ sx: { width: 280, bgcolor: 'primary.main', color: 'white' } }}>

      <Box sx={{ display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', p: 2,
        borderBottom: '1px solid rgba(255,255,255,0.15)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {resolveLogoSrc(clubInfo?.logo_url)
            ? <img src={resolveLogoSrc(clubInfo.logo_url)} alt="logo"
                style={{ width: 34, height: 34, borderRadius: 8, objectFit: 'cover',
                  border: '2px solid rgba(255,255,255,0.3)' }} />
            : user.is_platform_admin
              ? <img src="/assets/lapistics_logo_full.png" alt="Lapistics"
                  style={{ height: 30, width: 'auto', objectFit: 'contain' }} />
              : <Box sx={{ width: 34, height: 34, borderRadius: '8px',
                  border: '2px solid rgba(255,255,255,0.3)',
                  bgcolor: 'rgba(255,255,255,0.12)', flexShrink: 0 }} />
          }
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'white', lineHeight: 1.2 }}>
              {clubInfo?.name || (user.is_platform_admin ? 'Platform Admin' : '')}
            </Typography>
            {clubInfo?.name && (
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.62rem' }}>
                Lapistics
              </Typography>
            )}
          </Box>
        </Box>
        <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'white' }}>{user.name}</Typography>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>{user.role}</Typography>
      </Box>

      {user.is_platform_admin && (
        <Box sx={{ px: 2, py: 1, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <Chip icon={<LocationOn sx={{ fontSize: '0.85rem !important', color: '#FFD700 !important' }} />}
            label={selectedClubName || 'No club selected'} size="small"
            sx={{ bgcolor: 'rgba(255,215,0,0.12)', border: '1px solid rgba(255,215,0,0.45)',
              color: '#FFD700', fontWeight: 600, fontSize: '0.75rem', mb: 0.75 }} />
          <Button fullWidth size="small" startIcon={<SwapHoriz fontSize="small" />}
            onClick={() => go('/platform/select-club')}
            sx={{ color: 'rgba(255,255,255,0.85)', border: '1px solid rgba(255,255,255,0.25)',
              fontSize: '0.72rem', py: 0.4, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
            Switch Club
          </Button>
        </Box>
      )}

      <List sx={{ pt: 1, flexGrow: 1 }}>
        {!isPlatformAdminNoClub && visibleItems.map(item => {
          const active = location.pathname === item.path;
          return (
            <ListItem key={item.path} onClick={() => go(item.path)}
              sx={{ borderRadius: 2, mx: 1, mb: 0.5, width: 'calc(100% - 16px)',
                bgcolor: active ? 'rgba(255,255,255,0.18)' : 'transparent',
                cursor: 'pointer', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
              <ListItemText primary={item.label}
                primaryTypographyProps={{ fontSize: '0.9rem',
                  fontWeight: active ? 700 : 400, color: 'white' }} />
            </ListItem>
          );
        })}
        {user.is_platform_admin && (
          <>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)', my: 1 }} />
            <ListItem onClick={() => {
              selectClub(null);
              localStorage.removeItem('selected_club_id');
              localStorage.removeItem('selected_club_name');
              localStorage.removeItem('club');
              go('/platform-admin');
            }}
              sx={{ borderRadius: 2, mx: 1, mb: 0.5, width: 'calc(100% - 16px)',
                bgcolor: 'rgba(255,215,0,0.1)', border: '1px solid rgba(255,215,0,0.35)',
                cursor: 'pointer', '&:hover': { bgcolor: 'rgba(255,215,0,0.2)' } }}>
              <ListItemIcon sx={{ color: '#FFD700', minWidth: 32 }}>
                <Shield fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Platform Admin"
                primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 600, color: '#FFD700' }} />
            </ListItem>
          </>
        )}
      </List>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)' }} />
      <List>
        <ListItem onClick={() => go('/help')}
          sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
          <ListItemIcon sx={{ color: 'white', minWidth: 36 }}><HelpOutline fontSize="small" /></ListItemIcon>
          <ListItemText primary="Help"
            primaryTypographyProps={{ fontSize: '0.9rem', color: 'white' }} />
        </ListItem>
        <ListItem onClick={handleLogout}
          sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
          <ListItemIcon sx={{ color: 'white', minWidth: 36 }}><Logout fontSize="small" /></ListItemIcon>
          <ListItemText primary="Logout"
            primaryTypographyProps={{ fontSize: '0.9rem', color: 'white' }} />
        </ListItem>
      </List>
      <Box sx={{ px: 2, py: 1, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.7rem' }}>
          {formatTime(currentTime)}
        </Typography>
      </Box>
    </Drawer>
  );

  // ────────────────────────────────────────────
  // DESKTOP APPBAR
  // ────────────────────────────────────────────
  return (
    <>
      {drawer}
      <AppBar position="fixed" elevation={0}
        sx={{ bgcolor: 'primary.main', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <Toolbar sx={{ minHeight: { xs: 56, sm: 60 }, px: { xs: 1.5, sm: 2 } }}>

          {/* Mobile hamburger */}
          {isMobile && (
            <IconButton color="inherit" onClick={() => setDrawerOpen(true)} sx={{ mr: 1 }}>
              <MenuIcon />
            </IconButton>
          )}

          {/* ── LEFT: logo + club name ── */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1,
            flexShrink: 0, cursor: 'pointer', mr: { xs: 1, md: 2 } }}
            onClick={() => navigate('/dashboard')}>

            {/* Logo:
               - Platform admin (no club context): Lapistics logo
               - Club user with logo loaded: club logo
               - Club user while logo loading: blank placeholder (never show Lapistics)
            */}
            {user.is_platform_admin
              ? <img
                  src="/assets/lapistics_logo_full.png"
                  alt="Lapistics"
                  style={{ height: 32, width: 'auto', objectFit: 'contain' }}
                />
              : resolveLogoSrc(logoUrl)
                ? <img src={resolveLogoSrc(logoUrl)} alt="logo"
                    style={{ width: 28, height: 28, borderRadius: 6, objectFit: 'cover',
                      border: '2px solid rgba(255,255,255,0.3)' }} />
                : <Box sx={{ width: 28, height: 28, borderRadius: '6px',
                    border: '2px solid rgba(255,255,255,0.3)',
                    bgcolor: 'rgba(255,255,255,0.12)', flexShrink: 0 }} />
            }

            {/* Brand name:
               - Platform admin (any state): hide text — logo alone + optional club chip is enough
               - Regular user: show club name */}
            {!user.is_platform_admin && clubInfo?.name && (
              <Typography sx={{ fontWeight: 700, color: 'white', fontSize: '0.92rem',
                lineHeight: 1, display: { xs: 'none', sm: 'block' } }}>
                {clubInfo.name}
              </Typography>
            )}

            {/* Platform admin: selected club shown as small gold chip beside logo */}
            {!isMobile && user.is_platform_admin && selectedClubName && (
              <Chip
                label={selectedClubName}
                size="small"
                onClick={e => { e.stopPropagation(); navigate('/platform/select-club'); }}
                sx={{
                  bgcolor   : 'rgba(255,215,0,0.15)',
                  border    : '1px solid rgba(255,215,0,0.45)',
                  color     : '#FFD700',
                  fontWeight: 700,
                  fontSize  : '0.7rem',
                  height    : 22,
                  '& .MuiChip-label': { px: 1 },
                  '&:hover' : { bgcolor: 'rgba(255,215,0,0.28)' },
                }}
              />
            )}
          </Box>

          {/* ── MIDDLE: nav items (desktop only) OR spacer when no club ── */}
          {!isMobile && !isPlatformAdminNoClub && (
            <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden',
              minWidth: 0, gap: 0 }}>
              {visibleItems.map(item => {
                const active = location.pathname === item.path;
                return (
                  <Button key={item.path} color="inherit"
                    onClick={() => navigate(item.path)}
                    sx={{
                      px         : 1.1,
                      py         : 0.6,
                      fontSize   : '0.78rem',
                      fontWeight : active ? 700 : 400,
                      whiteSpace : 'nowrap',
                      flexShrink : 1,
                      minWidth   : 0,
                      opacity    : active ? 1 : 0.78,
                      bgcolor    : active ? 'rgba(255,255,255,0.14)' : 'transparent',
                      borderRadius: 1.5,
                      lineHeight : 1.2,
                      '&:hover'  : { bgcolor: 'rgba(255,255,255,0.1)', opacity: 1 },
                    }}>
                    {item.label}
                  </Button>
                );
              })}
            </Box>
          )}

          {/* Spacer: platform admin on /platform-admin (no nav items shown) */}
          {!isMobile && isPlatformAdminNoClub && <Box sx={{ flexGrow: 1 }} />}

          {/* Mobile spacer */}
          {isMobile && <Box sx={{ flexGrow: 1 }} />}

          {/* ── RIGHT: Platform Admin | Switch | clock | 🔔 | avatar ── */}
          <Box sx={{ display: 'flex', alignItems: 'center',
            gap: 0.75, flexShrink: 0, ml: 1 }}>

            {/* Platform Admin button — desktop */}
            {!isMobile && user.is_platform_admin && (
              <Button size="small"
                startIcon={<Shield sx={{ fontSize: '0.8rem !important' }} />}
                onClick={() => {
                  // Exit club context — platform admin is back at the top level
                  selectClub(null);
                  localStorage.removeItem('selected_club_id');
                  localStorage.removeItem('selected_club_name');
                  localStorage.removeItem('club');
                  navigate('/platform-admin');
                }}
                sx={{
                  bgcolor   : location.pathname === '/platform-admin'
                    ? 'rgba(255,215,0,0.3)' : 'rgba(255,215,0,0.12)',
                  border    : '1px solid rgba(255,215,0,0.45)',
                  color     : '#FFD700',
                  fontWeight: 700,
                  fontSize  : '0.73rem',
                  px: 1.1, py: 0.4,
                  whiteSpace: 'nowrap',
                  '&:hover' : { bgcolor: 'rgba(255,215,0,0.25)' },
                }}>
                Platform Admin
              </Button>
            )}

            {/* Switch club — desktop, platform admin */}
            {!isMobile && user.is_platform_admin && (
              <Button size="small"
                onClick={() => navigate('/platform/select-club')}
                sx={{
                  color    : 'rgba(255,255,255,0.85)',
                  border   : '1px solid rgba(255,255,255,0.28)',
                  fontSize : '0.7rem',
                  px: 0.9, py: 0.35,
                  whiteSpace: 'nowrap',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                }}>
                Switch
              </Button>
            )}

            {/* Clock — desktop, regular admin */}
            {!isMobile && !user.is_platform_admin && (
              <Typography variant="caption"
                sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.7rem',
                  fontWeight: 500, whiteSpace: 'nowrap', mr: 0.5 }}>
                {formatTime(currentTime)}
              </Typography>
            )}

            {/* Help — desktop, all roles */}
            {!isMobile && (
              <Tooltip title="Help">
                <IconButton color="inherit" size="small" onClick={() => navigate('/help')}>
                  <HelpOutline fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {/* Notifications bell */}
            <IconButton color="inherit" size="small"
              onClick={e => setNotifAnchor(e.currentTarget)}>
              <Badge badgeContent={unreadCount} color="error">
                <Notifications fontSize="small" />
              </Badge>
            </IconButton>

            {/* Avatar — desktop */}
            {!isMobile && (
              <IconButton onClick={e => setAnchorEl(e.currentTarget)} sx={{ p: 0.25 }}>
                <Avatar sx={{ width: 28, height: 28,
                  bgcolor: 'secondary.main', fontSize: 12 }}>
                  {user.name?.charAt(0)?.toUpperCase() || 'U'}
                </Avatar>
              </IconButton>
            )}
          </Box>

        </Toolbar>
      </AppBar>

      {/* Notifications dropdown */}
      <Menu anchorEl={notifAnchor} open={Boolean(notifAnchor)}
        onClose={() => setNotifAnchor(null)}
        PaperProps={{ sx: { maxWidth: 340, maxHeight: 400, width: '90vw' } }}>
        {notifications.length === 0 ? (
          <MenuItem disabled>No notifications</MenuItem>
        ) : [
          unreadCount > 0 && (
            <MenuItem key="mark-all"
              onClick={async () => {
                await Promise.all(notifications.filter(n => !n.is_read)
                  .map(n => markNotificationRead(n.id)));
                loadNotifications();
              }}
              sx={{ justifyContent: 'center', borderBottom: '1px solid',
                borderColor: 'divider' }}>
              <Typography variant="caption" color="primary" fontWeight={600}>
                Mark all as read ({unreadCount})
              </Typography>
            </MenuItem>
          ),
          ...notifications.slice(0, 20).map(n => (
            <MenuItem key={n.id} onClick={() => handleNotifClick(n)}
              sx={{ bgcolor: n.is_read ? 'transparent' : 'rgba(0,119,182,0.05)',
                whiteSpace: 'normal', maxWidth: 340 }}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: n.is_read ? 400 : 600 }}>
                  {n.message}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(n.created_at).toLocaleString()}
                </Typography>
              </Box>
            </MenuItem>
          ))
        ]}
      </Menu>

      {/* User dropdown */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}>
        <MenuItem disabled>
          <Box>
            <Typography variant="body2" fontWeight={600}>{user.name}</Typography>
            <Typography variant="caption" color="text.secondary">{user.role}</Typography>
          </Box>
        </MenuItem>
        <MenuItem onClick={() => { setAnchorEl(null); navigate('/help'); }}>
          <HelpOutline fontSize="small" sx={{ mr: 1 }} /> Help
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <Logout fontSize="small" sx={{ mr: 1 }} /> Logout
        </MenuItem>
      </Menu>
    </>
  );
}
